import { useContext, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { MainContext } from '../contexts/main';
import { getBillingSchema, getDefaultBillingValues, hasVatFormatError } from '../schemas/billingSchema';
import { generateBudget, checkNameVat } from '../helpers/api';
import { checkNIF } from '../helpers/functions';
import { getText } from '@educaplay/app-core/language';

export const useBillingForm = (initialData) => {
    const { mainReducer, environment } = useContext(MainContext);
    const [wantsAnInvoice, setWantsAnInvoice] = useState(initialData?.wantsAnInvoice ?? false);
    const budgetInvalidated = useRef(false);
    const mode = initialData?.mode || 'standard';

    const invoiceSchema = useMemo(() => getBillingSchema(), []);

    const methods = useForm({
        resolver: zodResolver(invoiceSchema),
        mode: 'onTouched',
        defaultValues: getDefaultBillingValues(initialData),
    });

    const { handleSubmit, watch, reset, trigger, setValue, getValues, setError, clearErrors, formState: { errors, isDirty, isValid } } = methods;
    const formValues = watch();
    const nameVatDebounceTimer = useRef(null);

    useEffect(() => {
        if (nameVatDebounceTimer.current) {
            clearTimeout(nameVatDebounceTimer.current);
        }

        const { country, vat_number, name } = formValues;

        if (country !== 'ES' || !vat_number || !name || !checkNIF(vat_number)) {
            if (errors.name?.type === 'manual') {
                clearErrors('name');
            }
            return;
        }

        nameVatDebounceTimer.current = setTimeout(async () => {
            const nameVatValid = await checkNameVat({ environment: environment.entorno, vat_number, name });
            if (!nameVatValid) {
                setError('name', {
                    type: 'manual',
                    message: getText('error_field_vat_name_mismatch'),
                });
            } else {
                if (errors.name?.type === 'manual') {
                    clearErrors('name');
                }
            }
        }, 800);

        return () => {
            if (nameVatDebounceTimer.current) {
                clearTimeout(nameVatDebounceTimer.current);
            }
        };
    }, [formValues.name, formValues.vat_number, formValues.country, environment, setError, clearErrors, errors.name]);

    // Limpiar error de vat_number cuando el país no es España
    // (salvo si el error es por CIF bloqueado o caracteres inválidos, que aplican a todos los países)
    useEffect(() => {
        if (formValues.country !== 'ES' && errors.vat_number && !hasVatFormatError(formValues.vat_number)) {
            clearErrors('vat_number');
        }
    }, [formValues.country, formValues.vat_number, errors.vat_number, clearErrors]);

    const triggerValidation = useCallback(async () => {
        const isFormValid = await trigger();
        if (!isFormValid) {
            return { valid: false, error: 'Formulario con errores de validación' };
        }

        const { country, vat_number, name } = getValues();
        if (country === 'ES' && vat_number && name && checkNIF(vat_number)) {
            const nameVatValid = await checkNameVat({ environment: environment.entorno, vat_number, name });
            if (!nameVatValid) {
                setError('name', {
                    type: 'manual',
                    message: getText('error_field_vat_name_mismatch'),
                });
                return { valid: false, error: 'Formulario con errores de validación' };
            }
        }

        return { valid: true };
    }, [trigger, getValues, environment, setError]);

    const validateAndGenerateBudget = useCallback(() => {
        return new Promise((resolve, reject) => {
            handleSubmit(
                async (data) => {
                    try {
                        // Validar nombre + NIF contra la API antes de continuar
                        if (data.country === 'ES' && data.vat_number && data.name && checkNIF(data.vat_number)) {
                            const nameVatValid = await checkNameVat({ environment: environment.entorno, vat_number: data.vat_number, name: data.name });
                            if (!nameVatValid) {
                                setError('name', {
                                    type: 'manual',
                                    message: getText('error_field_vat_name_mismatch'),
                                });
                                reject(new Error('Formulario con errores de validación'));
                                return;
                            }
                        }

                        const budgetData = {
                            selectedLicense: {
                                ...mainReducer.state.selectedLicense,
                                wantsAnInvoice,
                                country: data.country,
                                name: data.name,
                                vat_number: data.vat_number,
                                address: data.address,
                                town: data.town,
                                province: data.province,
                                zip_code: data.zip_code,
                            },
                            environment: environment.entorno,
                            user: mainReducer.state.user.id,
                            budget: environment.budget
                        };

                        const result = await generateBudget(budgetData);

                        mainReducer.dispatch({
                            type: 'setBudgetGenerated',
                            payload: result
                        });

                        reset(data);
                        resolve(result);
                    } catch (error) {
                        reject(error);
                    }
                },
                (errors) => {
                    reject(new Error('Formulario con errores de validación'));
                }
            )();
        });
    }, [handleSubmit, mainReducer, environment, wantsAnInvoice, reset, setError]);

    // Invalidar presupuesto cuando hay cambios
    useEffect(() => {
        if (isDirty && !budgetInvalidated.current && mainReducer.state.budgetGenerated !== false) {
            budgetInvalidated.current = true;
            mainReducer.dispatch({ type: 'invalidateBudget' });
        } else if (!isDirty) {
            budgetInvalidated.current = false;
        }
    }, [isDirty, mainReducer.state.budgetGenerated]);

    useEffect(() => {
        if (mainReducer.state.budgetGenerated !== false && mode !== 'edit') {
            budgetInvalidated.current = false;
            reset(getValues(), { keepErrors: true, keepTouched: true });
        }
    }, [mainReducer.state.budgetGenerated, mode, reset, getValues]);

    useEffect(() => {
        if (mainReducer.state.budgetGenerated !== false) {
            mainReducer.dispatch({ type: 'invalidateBudget' });
        }
    }, [wantsAnInvoice]);

    const prevCountry = useRef(formValues.country);
    const prevVatNumber = useRef(formValues.vat_number);
    const debounceTimer = useRef(null);
    const mainReducerRef = useRef(mainReducer);
    const wantsAnInvoiceRef = useRef(wantsAnInvoice);
    const environmentRef = useRef(environment);

    useEffect(() => {
        mainReducerRef.current = mainReducer;
    }, [mainReducer]);

    useEffect(() => {
        wantsAnInvoiceRef.current = wantsAnInvoice;
    }, [wantsAnInvoice]);

    useEffect(() => {
        environmentRef.current = environment;
    }, [environment]);

    useEffect(() => {
        const countryChanged = prevCountry.current !== formValues.country;
        const vatNumberChanged = prevVatNumber.current !== formValues.vat_number;

        prevCountry.current = formValues.country;
        prevVatNumber.current = formValues.vat_number;

        if (countryChanged && formValues.country) {
            setTimeout(() => {
                setValue('province', '', {
                    shouldValidate: false,
                    shouldDirty: true,
                    shouldTouch: true
                });
            }, 0);
        }

        if ((countryChanged || vatNumberChanged) && formValues.country && mainReducerRef.current.state.selectedLicense && mode !== 'edit') {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }

            debounceTimer.current = setTimeout(async () => {
                try {
                    const currentValues = getValues();
                    const budgetData = {
                        selectedLicense: {
                            ...mainReducerRef.current.state.selectedLicense,
                            wantsAnInvoice: wantsAnInvoiceRef.current,
                            country: currentValues.country,
                            name: currentValues.name || '',
                            vat_number: currentValues.vat_number || '',
                            address: currentValues.address || '',
                            town: currentValues.town || '',
                            province: '',
                            zip_code: currentValues.zip_code || '',
                        },
                        environment: environmentRef.current.entorno,
                        user: mainReducerRef.current.state.user.id,
                        budget: environmentRef.current.budget
                    };

                    const result = await generateBudget(budgetData);

                    if (result) {
                        mainReducerRef.current.dispatch({
                            type: 'setBudgetGenerated',
                            payload: result
                        });
                    }
                } catch (error) {
                }
            }, 800);
        }

        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, [formValues.country, formValues.vat_number, setValue, getValues, mode]);

    return {
        methods,
        formValues,
        wantsAnInvoice,
        setWantsAnInvoice,
        validateAndGenerateBudget,
        triggerValidation,
        hasErrors: Object.keys(errors).length > 0,
        isValid,
        errors
    };
};
